import React, { useEffect, useMemo, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, Select, TextInput, fmtDate, panelPost, useAdminGet } from '../kit';
import { Modal } from '../email/emailKit';
import { Pill, num, rowsOf, str, useDebounced } from '../ipintel/shared';
import { OpenLink, S, Toggle } from './shared';

/**
 * URLs & SEO — the editable URL structure and everything around it
 * (GET tech_pages/settings, POST tech_pages/settings_save — partial saves):
 *  - the URL patterns, with token help, the server's rules checked as you
 *    type, live example paths and the nginx prefix warning; a pattern change
 *    rebuilds every URL and the old ones keep answering 301;
 *  - the SEO title / description templates and the default share image;
 *  - the public API's rate limits;
 *  - the switch that closes the legacy /api/v1/technologies/{id} hole;
 *  - "Rebuild URLs" (POST tech_pages/rebuild_paths) and a path search with a
 *    per-page slug override (GET tech_pages/paths, POST tech_pages/path_override).
 */

const TOKENS = ['university', 'title', 'id', 'code'] as const;
const TOKEN_HELP: Record<string, string> = {
  university: 'the university slug — from its website label (else its name), e.g. iit-bombay',
  title: 'the title slug — from utech_title, at most 80 characters; a duplicate gets -<number>',
  id: 'the tech id number part, e.g. agi-000006 (never changes)',
  code: 'the university code without UTECH-, e.g. agi (never changes)',
};
const RESERVED = ['/admin', '/about', '/api', '/ip-intel-report', '/sso-callback', '/privacy-policy',
  '/terms-of-service', '/comet', '/rd-symposium', '/static', '/assets'];

const shapeOf = (p: string) => p.replace(/\{[a-z]+\}/g, '*');

/** The server's pattern rules (tech_pages validation), checked client-side for instant feedback. */
export const patternErrors = (kind: 'tech' | 'university', p: string, other: string): string[] => {
  const e: string[] = [];
  const v = p.trim();
  if (!v) return ['Required.'];
  if (!v.startsWith('/')) e.push('Must start with "/".');
  const tokens = Array.from(v.matchAll(/\{([^}]*)\}/g)).map(m => m[1]);
  const unknown = tokens.filter(t => !(TOKENS as readonly string[]).includes(t));
  if (unknown.length) e.push(`Unknown token${unknown.length > 1 ? 's' : ''}: ${unknown.map(t => `{${t}}`).join(', ')}.`);
  const literal = v.replace(/\{[a-z]+\}/g, '');
  if (/[^a-z0-9\-/{}]/.test(literal)) e.push('Only lowercase letters, digits, "-" and "/" outside the tokens.');
  if (/\/\//.test(v)) e.push('No empty segment ("//").');
  if (kind === 'tech' && !tokens.includes('title') && !tokens.includes('id')) e.push('Must contain {title} or {id}.');
  if (kind === 'university' && !tokens.includes('university') && !tokens.includes('code')) e.push('Must contain {university} or {code}.');
  const low = v.toLowerCase();
  const hit = RESERVED.find(r => low === r || low.startsWith(`${r}/`) || low.startsWith(`${r}{`));
  if (hit) e.push(`Cannot start with ${hit} — that path belongs to another page.`);
  if (kind === 'university' && low.replace(/\/+$/, '') === '/universities') e.push('Cannot be /universities itself (that is the directory page).');
  if (other.trim() && shapeOf(v) === shapeOf(other.trim())) e.push('Tech and university URLs must not have the same shape.');
  return e;
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Token values out of a path produced by `pattern`, or null when it does not match. */
const extract = (pattern: string, path: string): Record<string, string> | null => {
  const names: string[] = [];
  let re = '^';
  let last = 0;
  const rx = /\{([a-z]+)\}/g;
  let m: RegExpExecArray | null;
  while ((m = rx.exec(pattern))) {
    re += escapeRe(pattern.slice(last, m.index)) + '([a-z0-9-]+?)';
    names.push(m[1]);
    last = m.index + m[0].length;
  }
  re += `${escapeRe(pattern.slice(last))}$`;
  const hit = new RegExp(re).exec(path);
  if (!hit) return null;
  const out: Record<string, string> = {};
  names.forEach((n, i) => { out[n] = hit[i + 1]; });
  return out;
};

const fill = (pattern: string, vals: Record<string, string>) =>
  pattern.replace(/\{([a-z]+)\}/g, (_, t) => vals[t] || `{${t}}`);

/** Client copy of the slug rule, for the override preview; the server slugifies again. */
export const slugify = (s: string): string => {
  let out = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  if (out.length > 80) {
    const cut = out.slice(0, 81);
    const i = cut.lastIndexOf('-');
    out = i > 0 ? cut.slice(0, i) : out.slice(0, 80);
  }
  return out.replace(/-+$/, '');
};

const literalPrefix = (p: string) => p.split('{')[0];

// ---------------------------------------------------------------------------
const UrlPatterns: React.FC<{ settings: any; onSaved: (s: any) => void }> = ({ settings, onSaved }) => {
  const saved = settings?.url_patterns || {};
  const [tech, setTech] = useState(str(saved.tech));
  const [uni, setUni] = useState(str(saved.university));
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => { setTech(str(saved.tech)); setUni(str(saved.university)); }, [saved.tech, saved.university]);

  const techErr = patternErrors('tech', tech, uni);
  const uniErr = patternErrors('university', uni, tech);
  const dirty = tech.trim() !== str(saved.tech) || uni.trim() !== str(saved.university);
  const nginx: string[] = Array.isArray(settings?.nginx_prefixes) ? settings.nginx_prefixes.map(str) : [];
  // nginx matches by prefix: every URL of a pattern starts with its literal prefix.
  const uncovered = [['tech', tech], ['university', uni]].filter(([, p]) =>
    nginx.length > 0 && !nginx.some(n => literalPrefix(p.trim()).startsWith(n)));

  const examples: any[] = Array.isArray(settings?.examples) ? settings.examples : [];
  const preview = examples.map(ex => {
    const kind = str(ex?.kind) === 'university' ? 'university' : 'tech';
    const target = str(ex?.target);
    const cur = str(ex?.path);
    const vals: Record<string, string> = { ...(extract(kind === 'tech' ? str(saved.tech) : str(saved.university), cur) || {}) };
    const parts = target.toLowerCase().replace(/^utech-/, '').split('-');
    if (!vals.code) vals.code = parts[0] || '';
    if (kind === 'tech' && !vals.id) vals.id = parts.slice(0, 2).join('-');
    if (!vals.university) vals.university = vals.code;
    if (kind === 'tech' && !vals.title) vals.title = 'title-slug';
    const next = fill(kind === 'tech' ? tech.trim() : uni.trim(), vals);
    return { kind, target, cur, next, changed: next !== cur };
  });
  const changedKinds = [tech.trim() !== str(saved.tech) ? 'tech' : '', uni.trim() !== str(saved.university) ? 'university' : ''].filter(Boolean);

  const save = async () => {
    setConfirm(false); setBusy(true); setMsg(null);
    const { data, error } = await panelPost(S, 'settings_save', { url_patterns: { tech: tech.trim(), university: uni.trim() } });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    if (data) onSaved(data);
    const counts = data?.last_rebuild?.counts;
    setMsg({ kind: 'ok', text: `Saved — URLs rebuilt${counts ? ` (${Object.entries(counts).map(([k, v]) => `${k.replace(/_/g, ' ')} ${num(v).toLocaleString()}`).join(', ')})` : ''}. Old URLs now answer 301 to the new ones.` });
  };

  const field = (label: string, value: string, set: (v: string) => void, errs: string[], ph: string) => (
    <label className="flex flex-col gap-1 min-w-[16rem] flex-1">
      <span className="text-xs text-grayscale-1200 font-medium">{label}</span>
      <input value={value} onChange={e => set(e.target.value)} placeholder={ph} spellCheck={false}
        className={`h-9 px-3 rounded-md border-[0.5px] bg-white text-sm font-mono ${errs.length ? 'border-red-400' : 'border-primary-300'}`} />
      {errs.map(e => <span key={e} className="text-[11px] text-red-600">{e}</span>)}
    </label>
  );

  return (
    <Section title="URL structure"
      right={
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[11px] text-amber-700">unsaved</span>}
          <Btn small onClick={() => { setTech(str(saved.tech)); setUni(str(saved.university)); }} disabled={!dirty || busy}>Discard</Btn>
          <Btn small kind="primary" disabled={!dirty || busy || techErr.length > 0 || uniErr.length > 0}
            onClick={() => setConfirm(true)}>{busy ? 'Saving & rebuilding…' : 'Save & rebuild URLs'}</Btn>
        </div>
      }>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="flex gap-3 flex-wrap">
        {field('Technology page', tech, setTech, techErr, '/tech/{university}/{title}')}
        {field('University page', uni, setUni, uniErr, '/universities/{university}')}
      </div>
      <div className="rounded-lg bg-grayscale-200 px-3 py-2 text-xs text-grayscale-1200 flex flex-col gap-0.5">
        {TOKENS.map(t => <div key={t}><span className="font-mono font-bold">{`{${t}}`}</span> — {TOKEN_HELP[t]}</div>)}
        <div className="text-grayscale-1100 mt-1">
          {'{title}'} and {'{university}'} read best in search results; {'{id}'} / {'{code}'} never change. A per-page
          override (below) replaces {'{title}'} or {'{university}'} for that one page.
        </div>
      </div>

      {preview.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-xs font-medium text-grayscale-1200">Example URLs {dirty ? '(now → with your changes)' : ''}</div>
          <ul className="flex flex-col gap-1 text-xs">
            {preview.map(p => (
              <li key={`${p.kind}-${p.target}`} className="flex items-center gap-2 flex-wrap">
                <Pill tone="neutral">{p.kind === 'university' ? 'University' : 'Tech'}</Pill>
                <span className="font-mono break-all">{p.cur}</span>
                {dirty && p.changed && <><span className="text-grayscale-1100">→</span><span className="font-mono break-all text-primary-700">{p.next}</span></>}
                {!dirty && <OpenLink path={p.cur} label="↗" />}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={`rounded-lg border px-3 py-2 text-xs ${uncovered.length ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-grayscale-300 text-grayscale-1200'}`}>
        <b>Frontend nginx must match.</b> The production nginx (13.233.195.26, applied by hand) sends crawlers on these
        prefixes to the SEO renderer: {nginx.length ? nginx.map(n => <span key={n} className="font-mono mx-0.5">{n}</span>) : <i>none reported</i>}.
        {uncovered.length > 0 && (
          <div className="mt-1">
            ⚠ {uncovered.map(([k, p]) => `${k} prefix ${literalPrefix(p.trim()) || '/'}`).join(' and ')} is not covered —
            update nginx before (or right after) saving, or crawlers and link previews get the bare app shell.
          </div>
        )}
      </div>

      {settings?.last_rebuild?.at && (
        <div className="text-[11px] text-grayscale-1100">
          Last rebuild {fmtDate(settings.last_rebuild.at)}
          {settings.last_rebuild.counts && ` · ${Object.entries(settings.last_rebuild.counts).map(([k, v]) => `${k.replace(/_/g, ' ')} ${num(v).toLocaleString()}`).join(' · ')}`}
        </div>
      )}

      {confirm && (
        <Modal title="Change the URL structure?" onClose={() => setConfirm(false)}
          footer={<>
            <Btn onClick={() => setConfirm(false)}>Cancel</Btn>
            <Btn kind="primary" onClick={save}>Save &amp; rebuild</Btn>
          </>}>
          <p>Every {changedKinds.map(k => (k === 'tech' ? 'technology' : 'university')).join(' and ')} page gets a new URL:</p>
          <ul className="text-xs font-mono flex flex-col gap-0.5">
            {tech.trim() !== str(saved.tech) && <li>{str(saved.tech)} → {tech.trim()}</li>}
            {uni.trim() !== str(saved.university) && <li>{str(saved.university)} → {uni.trim()}</li>}
          </ul>
          <p className="text-xs text-grayscale-1100">
            The old URLs keep working as permanent (301) redirects, so links and search results are not lost, but
            search engines take a while to re-index. The rebuild takes about 15 seconds.
            {uncovered.length > 0 && <b className="text-amber-800"> nginx does not cover the new prefix yet.</b>}
          </p>
        </Modal>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const SEO_FIELDS: { key: string; label: string; hint: string; area?: boolean }[] = [
  { key: 'tech_title_template', label: 'Tech page — title', hint: 'e.g. {title} | {university_label} | UniversityTech.io' },
  { key: 'tech_description_template', label: 'Tech page — description', hint: 'Used when the summary is not public pre-login', area: true },
  { key: 'university_title_template', label: 'University page — title', hint: '' },
  { key: 'university_description_template', label: 'University page — description', hint: '', area: true },
  { key: 'default_og_image', label: 'Default share image (URL)', hint: 'Shown by LinkedIn / WhatsApp / X link previews' },
];

const SeoTemplates: React.FC<{ settings: any; onSaved: (s: any) => void }> = ({ settings, onSaved }) => {
  const savedSeo = settings?.seo || {};
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const key = JSON.stringify(savedSeo);
  useEffect(() => { setDraft(Object.fromEntries(SEO_FIELDS.map(f => [f.key, str(savedSeo[f.key])]))); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = SEO_FIELDS.some(f => (draft[f.key] ?? '') !== str(savedSeo[f.key]));
  const tokens = Array.from(new Set(SEO_FIELDS.flatMap(f => Array.from(str(savedSeo[f.key]).matchAll(/\{([a-z_]+)\}/g)).map(m => m[1]))));

  const save = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost(S, 'settings_save', { seo: draft });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    if (data) onSaved(data);
    setMsg({ kind: 'ok', text: 'SEO templates saved.' });
  };
  return (
    <Section title="SEO templates"
      right={<Btn small kind="primary" onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</Btn>}>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <p className="text-xs text-grayscale-1100 -mt-1">
        What search engines and link previews show. Words in {'{braces}'} are filled per page
        {tokens.length ? <> — the current templates use {tokens.map(t => <span key={t} className="font-mono mx-0.5">{`{${t}}`}</span>)}</> : null}.
        The pre-login rules still apply: a summary that is locked before sign-in is never put in a description.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {SEO_FIELDS.map(f => (
          <label key={f.key} className="flex flex-col gap-1">
            <span className="text-xs text-grayscale-1200 font-medium">{f.label}</span>
            {f.area ? (
              <textarea rows={3} value={draft[f.key] ?? ''} placeholder={f.hint}
                onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
            ) : (
              <input value={draft[f.key] ?? ''} placeholder={f.hint}
                onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
            )}
            {f.key.endsWith('title_template') && draft[f.key] && (
              <span className={`text-[10px] ${(draft[f.key] || '').length > 70 ? 'text-amber-700' : 'text-grayscale-1100'}`}>
                {(draft[f.key] || '').length} characters in the template (Google shows about 60)
              </span>
            )}
          </label>
        ))}
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
/** rate_limits may be flat {anon_per_min: 30} or nested {anonymous: {per_min: 30}}; edited as flat paths. */
const flatten = (o: any, prefix = ''): Record<string, number> => {
  const out: Record<string, number> = {};
  if (!o || typeof o !== 'object') return out;
  Object.entries(o).forEach(([k, v]) => {
    const p = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object') Object.assign(out, flatten(v, p));
    else if (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)))) out[p] = Number(v);
  });
  return out;
};
const unflatten = (flat: Record<string, number>): any => {
  const out: any = {};
  Object.entries(flat).forEach(([p, v]) => {
    const parts = p.split('.');
    let cur = out;
    parts.slice(0, -1).forEach(k => { cur[k] = cur[k] || {}; cur = cur[k]; });
    cur[parts[parts.length - 1]] = v;
  });
  return out;
};
const humanKey = (p: string) => p.replace(/\./g, ' · ').replace(/_/g, ' ')
  .replace(/\bper min\b/, 'per minute').replace(/\banon\b/, 'anonymous');

const RateLimits: React.FC<{ settings: any; onSaved: (s: any) => void }> = ({ settings, onSaved }) => {
  const saved = useMemo(() => flatten(settings?.rate_limits), [settings?.rate_limits]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => { setDraft(Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, String(v)]))); }, [saved]);
  const keys = Object.keys(saved);
  const bad = keys.filter(k => !/^\d+$/.test((draft[k] ?? '').trim()) || Number(draft[k]) < 1);
  const dirty = keys.some(k => (draft[k] ?? '') !== String(saved[k]));
  const save = async () => {
    setBusy(true); setMsg(null);
    const flat = Object.fromEntries(keys.map(k => [k, Number(draft[k])]));
    const { data, error } = await panelPost(S, 'settings_save', { rate_limits: unflatten(flat) });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    if (data) onSaved(data);
    setMsg({ kind: 'ok', text: 'Rate limits saved.' });
  };
  return (
    <Section title="Rate limits (anti-scraping)"
      right={<Btn small kind="primary" onClick={save} disabled={!dirty || busy || bad.length > 0}>{busy ? 'Saving…' : 'Save'}</Btn>}>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <p className="text-xs text-grayscale-1100 -mt-1">
        Requests per client IP (signed out) or per account (signed in) to the public page API. Past a limit the page
        shows a friendly “slow down” message and the request is answered 429. Defaults: anonymous 30 / minute and
        400 / day, signed-in 120 / minute.
      </p>
      {keys.length === 0 ? <div className="text-xs text-grayscale-1100">The server reported no limits.</div> : (
        <div className="flex gap-3 flex-wrap">
          {keys.map(k => (
            <label key={k} className="flex flex-col gap-1 w-44">
              <span className="text-xs text-grayscale-1200 capitalize">{humanKey(k)}</span>
              <input type="number" min={1} value={draft[k] ?? ''} onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))}
                className={`h-9 px-3 rounded-md border-[0.5px] bg-white text-sm ${bad.includes(k) ? 'border-red-400' : 'border-primary-300'}`} />
            </label>
          ))}
        </div>
      )}
      {bad.length > 0 && <div className="text-[11px] text-red-600">Whole numbers of 1 or more only.</div>}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const LegacyGuard: React.FC<{ settings: any; onSaved: (s: any) => void }> = ({ settings, onSaved }) => {
  const on = !!settings?.protect_legacy_detail;
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const set = async (v: boolean) => {
    setConfirm(false); setBusy(true); setMsg(null);
    const { data, error } = await panelPost(S, 'settings_save', { protect_legacy_detail: v });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    if (data) onSaved(data);
    setMsg({ kind: 'ok', text: v ? 'Legacy detail endpoint is now protected.' : 'Protection turned off — the legacy endpoint is open again.' });
  };
  return (
    <Section title="Protect the legacy tech detail endpoint">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="flex items-start gap-3">
        <Toggle on={on} busy={busy} label="Protect legacy tech detail endpoint"
          onChange={v => (v ? setConfirm(true) : set(false))} />
        <div className="text-xs text-grayscale-1200 flex flex-col gap-1">
          <div><b>{on ? 'On' : 'Off'}</b> — <span className="font-mono">GET /api/v1/technologies/&#123;id&#125;</span> and
            <span className="font-mono"> /api/v1/researchers/&#123;id&#125;</span> {on
              ? 'answer 401 unless the caller is signed in, and never send full_text.'
              : 'answer anyone, with researcher e-mails and the raw scraped text — the scrape hole.'}</div>
          <div className="text-amber-800">
            Turn this on <b>only after the new frontend is deployed</b>: today's Cosmos tech card and Contact University
            pop-up call that endpoint without a sign-in token, so switching it on earlier breaks them for everyone.
          </div>
        </div>
      </div>
      {confirm && (
        <Modal title="Protect the legacy endpoint?" onClose={() => setConfirm(false)}
          footer={<>
            <Btn onClick={() => setConfirm(false)}>Cancel</Btn>
            <Btn kind="primary" onClick={() => set(true)}>Yes — the new frontend is live</Btn>
          </>}>
          <p>Is the frontend that sends the sign-in token with tech details already deployed on universitytech.io?</p>
          <p className="text-xs text-grayscale-1100">
            If not, the tech card and Contact University stop loading for every visitor until you turn this off again.
          </p>
        </Modal>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const PATH_COLS = ['path', 'kind', 'target', 'canonical', 'created_at', 'retired_at'];
const PATH_LABELS: Record<string, string> = {
  path: 'URL', kind: 'Kind', target: 'Page', canonical: 'Status', created_at: 'Issued', retired_at: 'Retired',
};

const OverrideEditor: React.FC<{ kind: string; target: string; onDone: (msg: string) => void; onClose: () => void }> =
  ({ kind, target, onDone, onClose }) => {
  const [raw, setRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const slug = slugify(raw);
  const post = async (value: string | null) => {
    setBusy(true); setErr('');
    const { data, error } = await panelPost(S, 'path_override', { kind, target, slug: value });
    setBusy(false);
    if (error) { setErr(error); return; }
    onDone(value === null
      ? `Override cleared for ${target} — URL is ${str(data?.canonical_path) || 'rebuilt from the title / label'}.`
      : `${target} now lives at ${str(data?.canonical_path) || slug}. The old URL redirects.`);
  };
  return (
    <div className="rounded-lg border border-primary-300 bg-primary-100/40 p-3 flex flex-col gap-2">
      <div className="text-xs text-grayscale-1200">
        Custom slug for <span className="font-mono font-bold">{target}</span> — replaces {kind === 'university' ? '{university}' : '{title}'} in its URL only.
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <TextInput value={raw} onChange={setRaw} placeholder={kind === 'university' ? 'e.g. agrinnovate-india' : 'e.g. freshwater-prawn-aquaculture'} wide />
        <Btn small kind="primary" disabled={busy || !slug} onClick={() => post(slug)}>{busy ? 'Saving…' : 'Set slug'}</Btn>
        <Btn small disabled={busy} onClick={() => post(null)} title="Go back to the slug generated from the title / label">Clear override</Btn>
        <Btn small disabled={busy} onClick={onClose}>Cancel</Btn>
      </div>
      {raw && <div className="text-[11px] text-grayscale-1100">Will be saved as <span className="font-mono">{slug || '—'}</span></div>}
      {err && <Msg kind="error">{err}</Msg>}
    </div>
  );
};

const PathSearch: React.FC<{ tick: number }> = ({ tick }) => {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [onlyCanon, setOnlyCanon] = useState(false);
  const [edit, setEdit] = useState<{ kind: string; target: string } | null>(null);
  const [msg, setMsg] = useState('');
  const [mine, setMine] = useState(0);
  const dq = useDebounced(q);
  const isId = /^utech-[a-z0-9]+(-\d+)?$/i.test(dq.trim());
  const params: Record<string, string | number> = { q: isId ? '' : dq.trim(), kind, per_page: 100 };
  if (isId) params.target = dq.trim().toUpperCase();
  if (onlyCanon) params.canonical = 1;
  const res = useAdminGet(S, 'paths', params, [JSON.stringify(params), tick, mine]);
  const rows = rowsOf(res.data, 'paths').filter((r: any) => !onlyCanon || r?.canonical);
  const total = num(res.data?.total ?? rows.length);
  return (
    <Section title="Find a URL / override a slug">
      <p className="text-xs text-grayscale-1100 -mt-1">
        Every URL ever issued. The current one per page is <b>canonical</b>; older ones answer 301 to it. Search by a
        piece of the URL, or paste a tech id / university code to see that page's whole history.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <TextInput value={q} onChange={setQ} wide placeholder="Search URL, or UTECH-AGI-000006 / UTECH-AGI…" />
        <Select value={kind} onChange={setKind} options={['', 'tech', 'university']}
          labels={{ '': 'Tech and university', tech: 'Tech pages', university: 'University pages' }} />
        <label className="inline-flex items-center gap-1.5 text-xs text-grayscale-1200 cursor-pointer whitespace-nowrap">
          <input type="checkbox" checked={onlyCanon} onChange={e => setOnlyCanon(e.target.checked)} />
          Only current URLs
        </label>
        <span className="text-xs text-grayscale-1100">{res.loading ? 'Searching…' : `${total.toLocaleString()} match${total === 1 ? '' : 'es'}`}</span>
      </div>
      {msg && <Msg kind="ok">{msg}</Msg>}
      {edit && (
        <OverrideEditor kind={edit.kind} target={edit.target} onClose={() => setEdit(null)}
          onDone={m => { setMsg(m); setEdit(null); setMine(x => x + 1); }} />
      )}
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading /> : (
        <DataTable rows={rows} columns={PATH_COLS} labels={PATH_LABELS} maxHeight="26rem"
          empty={dq ? 'No URL matches.' : 'No URLs issued yet — run "Rebuild URLs".'}
          render={{
            path: r => <span className="font-mono text-xs">{str(r.path)}</span>,
            kind: r => (str(r.kind) === 'university' ? 'University' : 'Tech'),
            target: r => (
              <button type="button" onClick={() => setQ(str(r.target))} title="Show this page's URL history"
                className="font-mono text-xs underline underline-offset-2">{str(r.target)}</button>
            ),
            canonical: r => (r.canonical ? <Pill tone="good">current</Pill> : <Pill tone="neutral">301 → current</Pill>),
            created_at: r => <span className="text-xs">{fmtDate(r.created_at)}</span>,
            retired_at: r => <span className="text-xs">{r.retired_at ? fmtDate(r.retired_at) : '—'}</span>,
          }}
          actions={r => (
            <span className="inline-flex items-center gap-2">
              {r.canonical && <Btn small onClick={() => { setMsg(''); setEdit({ kind: str(r.kind), target: str(r.target) }); }}>Override slug</Btn>}
              <OpenLink path={r.path} label="↗" />
            </span>
          )} />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const UrlsSeo: React.FC = () => {
  const res = useAdminGet(S, 'settings', {}, []);
  const [settings, setSettings] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { if (res.data) setSettings(res.data); }, [res.data]);
  const onSaved = (s: any) => { if (s && typeof s === 'object' && (s.url_patterns || s.seo || s.sections)) setSettings(s); else res.reload(); setTick(t => t + 1); };

  const rebuild = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost(S, 'rebuild_paths', {});
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const counts = data?.counts ?? data;
    const text = counts && typeof counts === 'object'
      ? Object.entries(counts).filter(([, v]) => typeof v === 'number' || typeof v === 'string')
        .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? v.toLocaleString() : v}`).join(' · ')
      : '';
    setMsg({ kind: 'ok', text: `URLs rebuilt${text ? ` — ${text}` : ''}.` });
    res.reload(); setTick(t => t + 1);
  };

  if (res.loading && !settings) return <Loading label="Reading the page settings…" />;
  if (res.error && !settings) return <Msg kind="error">{res.error}</Msg>;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Btn onClick={rebuild} disabled={busy} title="Recompute every canonical URL from the current patterns, labels, titles and overrides">
          {busy ? 'Rebuilding…' : 'Rebuild URLs'}
        </Btn>
        <span className="text-xs text-grayscale-1100">
          Runs by itself after a label, carousel or pattern save and at server start; use this after a corpus load.
        </span>
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <UrlPatterns settings={settings} onSaved={onSaved} />
      <PathSearch tick={tick} />
      <SeoTemplates settings={settings} onSaved={onSaved} />
      <RateLimits settings={settings} onSaved={onSaved} />
      <LegacyGuard settings={settings} onSaved={onSaved} />
    </div>
  );
};

export default UrlsSeo;
